import { z } from 'zod';

import type {
  PatchAdminV1StoresStoreIdRedirectsIdBody,
  PostAdminV1StoresStoreIdRedirectsBody,
  Redirect,
} from '@nucleus-commerce/api-client/admin';

export const REDIRECT_STATUS_CODES = [301, 302] as const;
export type RedirectStatusCode = (typeof REDIRECT_STATUS_CODES)[number];

const FROM_PATH = /^\/[^\s]*$/;

/** The backend's rule for a target: an absolute store path or a full https URL. */
export function isRedirectTarget(value: string): boolean {
  return value.startsWith('/') || value.startsWith('https://');
}

export const redirectFormSchema = z
  .object({
    fromPath: z
      .string()
      .trim()
      .min(1, { error: 'redirects:validation.fromRequired' })
      .max(500, { error: 'redirects:validation.fromLength' })
      .regex(FROM_PATH, { error: 'redirects:validation.fromFormat' }),
    toPath: z
      .string()
      .trim()
      .min(1, { error: 'redirects:validation.toRequired' })
      .max(1000, { error: 'redirects:validation.toLength' })
      .refine(isRedirectTarget, { error: 'redirects:validation.toFormat' }),
    statusCode: z.enum(['301', '302']),
  })
  .refine((v) => v.fromPath !== v.toPath, {
    path: ['toPath'],
    error: 'redirects:validation.selfTarget',
  });

export type RedirectForm = z.infer<typeof redirectFormSchema>;

export function emptyRedirectForm(): RedirectForm {
  return { fromPath: '', toPath: '', statusCode: '301' };
}

export function fromRedirect(redirect: Redirect): RedirectForm {
  return {
    fromPath: redirect.fromPath,
    toPath: redirect.toPath,
    statusCode: redirect.statusCode === 302 ? '302' : '301',
  };
}

const toStatusCode = (value: RedirectForm['statusCode']): RedirectStatusCode =>
  value === '302' ? 302 : 301;

export function toRedirectCreate(values: RedirectForm): PostAdminV1StoresStoreIdRedirectsBody {
  return {
    fromPath: values.fromPath,
    toPath: values.toPath,
    statusCode: toStatusCode(values.statusCode),
  };
}

/** The source path is the redirect's identity; only the target and the code can change. */
export function toRedirectUpdate(values: RedirectForm): PatchAdminV1StoresStoreIdRedirectsIdBody {
  return { toPath: values.toPath, statusCode: toStatusCode(values.statusCode) };
}
